<script setup lang="ts">
import type { VListCardBodyAttrs, VListCardBodySlotProps, VListCardProps } from "./VListCard.types";
import { computed, provide, useId } from "vue";
import { useI18n } from "vue-i18n";
import GenericListScene from "../../illustrations/empty/GenericListScene.vue";
import VButton from "../Button/VButton.vue";
import VEmptyState from "../EmptyState/VEmptyState.vue";
import VInput from "../Input/VInput.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import { ListCardPassthrough, ListCardRoleCells } from "./listCardRoles";
import { listCardBarCva, listCardBodyCva, listCardColumnsCva, listCardCva, listCardRowCva } from "./VListCard.cva";
import { LIST_CARD_KEY } from "./VListCard.types";

defineOptions({
  name: "VListCard",
});

/**
 * A bordered list: an optional header with a label and a count, an optional column header, rows divided by lines
 * and an optional footer. Rows are `VListCardRow`s or any element; the card draws the dividers between them.
 */
const {
  as = "div",
  title = undefined,
  count = null,
  columnsClass = undefined,
  maxHeight = undefined,
  loading = false,
  skeletonRows = 3,
  empty = false,
  emptyText = undefined,
  searchEmpty = false,
  searchEmptyText = undefined,
  searchable = false,
  searchPlaceholder = undefined,
  ariaLabel = undefined,
  ariaLabelledby = undefined,
  ariaDescribedby = undefined,
  invalid = false,
  dense = false,
  testId = undefined,
} = defineProps<VListCardProps>();

const slots = defineSlots<{
  /** Replaces the whole content of the header bar. */
  "header"?: () => unknown
  /** Replaces the label on the left of the header. Put `id` on the label and pass it as `ariaLabelledby` to name the list. */
  "title"?: (props: { id: string }) => unknown
  /** Replaces the count on the right of the header. */
  "count"?: () => unknown
  /** Controls on the right of the header, after the count (a "select all" button, a link). */
  "header-actions"?: () => unknown
  /** Replaces the built-in search field above the card. */
  "search"?: () => unknown
  /** Column labels; laid out on the `columnsClass` grid. With `as="table"` every label element is a column header. */
  "columns"?: () => unknown
  /** The rows. */
  "default"?: () => unknown
  /**
   * Replaces the list body while the rows show, such as a `VueDraggable` that is the list itself. Render the element
   * `tag` with `v-bind="attrs"` (dividers, scroll box, roles and names) and the rows inside it. Loading, empty and
   * search-empty states still render in the card's own body.
   */
  "body"?: (props: VListCardBodySlotProps) => unknown
  /** Replaces `emptyText` when `empty`. */
  "empty"?: () => unknown
  /** Replaces `searchEmptyText` when `searchEmpty`. */
  "search-empty"?: () => unknown
  /** Replaces the skeleton rows while `loading`. */
  "loading"?: () => unknown
  /** A bar under the rows, in the header's colours. */
  "footer"?: () => unknown
}>();

const search = defineModel<string>("search", { default: "" });

const { t } = useI18n();
const titleId = useId();

/*
 * Slots are not reactive: a slot that appears later (`<template v-if="..." #columns>`) is only seen when it is
 * checked during render. These checks are functions on purpose, so the template calls them on every render instead
 * of caching the first answer.
 */
function hasHeader(): boolean {
  return Boolean(
    title || (count !== null && count !== undefined && count !== "")
    || slots.header || slots.title || slots.count || slots["header-actions"],
  );
}
function hasColumns(): boolean {
  return Boolean(slots.columns);
}
function hasFooter(): boolean {
  return Boolean(slots.footer);
}

const isTable = computed(() => as === "table");
const isList = computed(() => as === "ul" || as === "ol");
const showRows = computed(() => !loading && !empty && !searchEmpty);
/**
 * The element of the body. A table is a `div` with ARIA table roles. A description list only holds rows: while a
 * state message replaces them, the body is a plain `div`.
 */
const bodyTag = computed(() => {
  if (isTable.value || (as === "dl" && !showRows.value)) {
    return "div";
  }
  return as;
});
/** A custom `title` slot names the list only when it renders the `id` it gets and passes it as `ariaLabelledby`. */
const labelledBy = computed(() => ariaLabelledby ?? (title ? titleId : undefined));
/**
 * A `div` body needs the group role to carry a name, a description or the invalid state; a ul or ol is announced as
 * a list. A `dl` has no role that takes a name, so it keeps its own semantics and the header stays visible text.
 */
const bodyRole = computed(() => {
  if (isTable.value) {
    return "rowgroup";
  }
  if (bodyTag.value === "div" && (labelledBy.value || ariaLabel || ariaDescribedby || invalid)) {
    return "group";
  }
  return undefined;
});
/** Name, description and invalid state of the list: on the table in table mode, otherwise on the body. */
const nameAttrs = computed(() => ({
  "aria-label": labelledBy.value ? undefined : ariaLabel,
  "aria-labelledby": labelledBy.value,
  "aria-describedby": ariaDescribedby,
  // aria-invalid belongs on a group of controls, not on a list or a table.
  "aria-invalid": invalid && bodyRole.value === "group" ? "true" as const : undefined,
}));
/** The body names the list, except in table mode (the table does) and in a `dl` (it cannot carry a name). */
const bodyNamesList = computed(() => !isTable.value && bodyTag.value !== "dl");
/** A message replaces the rows; inside a ul or ol it still has to be a list item, in a table a row with a cell. */
const messageTag = computed(() => (isList.value ? "li" : "div"));
const messageRole = computed(() => (isTable.value ? "row" : undefined));

const bodyStyle = computed(() => {
  if (maxHeight === undefined) {
    return undefined;
  }
  return { maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight };
});

const frameClass = computed(() => listCardCva({ invalid }));
const headerClass = computed(() => listCardBarCva({ first: true, last: false }));
function columnsClasses() {
  return [listCardColumnsCva({ first: !hasHeader() }), columnsClass];
}
function bodyClass() {
  return listCardBodyCva({
    first: !hasHeader() && !hasColumns(),
    last: !hasFooter(),
    scroll: maxHeight !== undefined,
  });
}
/** Everything the body element gets; also handed to the `body` slot for a body of its own. */
function bodyAttrs(): VListCardBodyAttrs {
  const names = bodyNamesList.value ? nameAttrs.value : undefined;
  return {
    "class": bodyClass(),
    "style": bodyStyle.value,
    "role": bodyRole.value,
    "aria-label": names?.["aria-label"],
    "aria-labelledby": names?.["aria-labelledby"],
    "aria-describedby": names?.["aria-describedby"],
    "aria-invalid": names?.["aria-invalid"],
    "aria-busy": loading && !isTable.value ? true : undefined,
    "data-testid": "list-card-body",
  };
}
function bodySlotProps(): VListCardBodySlotProps {
  return { tag: bodyTag.value, attrs: bodyAttrs() };
}
/** In table mode the column header and the body sit in an element with the table role, which names the list. */
const tableAttrs = computed(() => ({
  "class": "flex flex-col",
  "role": "table",
  ...nameAttrs.value,
  "aria-busy": loading || undefined,
  "data-testid": "list-card-table",
}));
const footerClass = computed(() => listCardBarCva({ first: false, last: true }));
const messageClass = computed(() => [listCardRowCva(), "m-0 text-gray-700 dark:text-gray-400"]);

provide(LIST_CARD_KEY, {
  tag: computed(() => as),
  columnsClass: computed(() => columnsClass),
  dense: computed(() => dense),
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <slot v-if="searchable || $slots.search" name="search">
      <VInput
        v-model="search"
        type="search"
        icon-left="search"
        :placeholder="searchPlaceholder ?? t('base.search')"
        :aria-label="searchPlaceholder ?? t('base.search')"
        :has-margin="false"
        data-testid="list-card-search"
      />
    </slot>

    <div :class="frameClass" :data-testid="testId" :data-invalid="invalid || undefined">
      <div v-if="hasHeader()" :class="headerClass" data-testid="list-card-header">
        <slot name="header">
          <span class="flex min-w-0 items-center gap-2">
            <slot :id="titleId" name="title">
              <span v-if="title" :id="titleId" class="truncate" data-testid="list-card-title">{{ title }}</span>
            </slot>
          </span>
          <span v-if="(count !== null && count !== '') || $slots.count || $slots['header-actions']" class="flex shrink-0 items-center gap-3">
            <slot name="count">
              <span
                v-if="count !== null && count !== ''"
                class="font-medium text-primary-700 dark:text-primary-400"
                data-testid="list-card-count"
              >
                {{ count }}
              </span>
            </slot>
            <slot name="header-actions" />
          </span>
        </slot>
      </div>

      <component :is="isTable ? 'div' : ListCardPassthrough" v-bind="isTable ? tableAttrs : {}">
        <div v-if="hasColumns()" :class="columnsClasses()" :role="isTable ? 'row' : undefined" data-testid="list-card-columns">
          <ListCardRoleCells :role="isTable ? 'columnheader' : undefined">
            <slot name="columns" />
          </ListCardRoleCells>
        </div>

        <slot v-if="showRows && $slots.body" name="body" v-bind="bodySlotProps()" />
        <component :is="bodyTag" v-else v-bind="bodyAttrs()">
          <template v-if="loading">
            <slot name="loading">
              <component
                :is="messageTag"
                v-for="index in skeletonRows"
                :key="index"
                :class="listCardRowCva({ dense })"
                :role="messageRole"
                data-testid="list-card-skeleton"
              >
                <component :is="isTable ? 'div' : ListCardPassthrough" v-bind="isTable ? { role: 'cell' } : {}">
                  <span v-if="index === 1" class="sr-only">{{ t("base.loading") }}</span>
                  <span class="flex h-5 items-center" aria-hidden="true">
                    <VSkeleton :height="14" :width="60" :dynamic-width="false" />
                  </span>
                </component>
              </component>
            </slot>
          </template>
          <component :is="messageTag" v-else-if="empty" :class="messageClass" :role="messageRole" data-testid="list-card-empty">
            <component :is="isTable ? 'div' : ListCardPassthrough" v-bind="isTable ? { role: 'cell' } : {}">
              <slot name="empty">
                <template v-if="emptyText">
                  {{ emptyText }}
                </template>
                <!-- Without a text of its own the list shows the general illustrated state of the board. -->
                <VEmptyState
                  v-else
                  :title="t('base.list_card.empty_title')"
                  :text="t('base.list_card.empty_text')"
                  state="empty"
                  variant="plain"
                  class="w-full"
                  :heading-level="3"
                >
                  <template #scene>
                    <GenericListScene />
                  </template>
                </VEmptyState>
              </slot>
            </component>
          </component>
          <component :is="messageTag" v-else-if="searchEmpty" :class="messageClass" :role="messageRole" data-testid="list-card-search-empty">
            <!-- The status role sits inside the row: an li of a ul or ol has to keep its listitem role. -->
            <component :is="isTable ? 'div' : ListCardPassthrough" v-bind="isTable ? { role: 'cell' } : {}">
              <div role="status" class="w-full">
                <slot name="search-empty">
                  <template v-if="searchEmptyText">
                    {{ searchEmptyText }}
                  </template>
                  <VEmptyState
                    v-else
                    :title="t('base.list_card.no_results_title')"
                    :text="t('base.list_card.no_results_text')"
                    state="no-results"
                    variant="plain"
                    class="w-full"
                    :heading-level="3"
                  >
                    <template #scene>
                      <GenericListScene />
                    </template>
                    <template v-if="search" #actions>
                      <VButton variant="gray" rounded data-testid="list-card-clear-search" @click="search = ''">
                        {{ t("base.table.state.clear_search") }}
                      </VButton>
                    </template>
                  </VEmptyState>
                </slot>
              </div>
            </component>
          </component>
          <slot v-if="showRows" />
        </component>
      </component>

      <div v-if="hasFooter()" :class="footerClass" data-testid="list-card-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>
